import React, { useEffect, useRef, useState } from "react";
import { Map, AdvancedMarker, Marker, useMap } from '@vis.gl/react-google-maps'
import moment from "moment/moment";
import { Card } from "@mui/material";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import convertUtils from 'convert-units';
import Polyline from "./Polyline";
import crudsService from "services/cruds-service";
import MapToolbar from "./MapToolbar";
import { useMediaQuery } from "react-responsive";
const mapContainerStyle = {
    width: "100%",
    height: "500px",
};
let pulseStyle = {
    position: "relative",
    width: "50px",
    height: "50px",
    backgroundColor: "rgba(0, 150, 255, 0.5)",
    borderRadius: "50%",
    animation: "pulse 1.5s infinite",
    top: '-45px',
    left: '-5px',
};
const iconContainerStyle = {
    width: '35px',
    height: '35px',
}

const generateColors = (count) => {
    const colors = ["#FF0000", "#00FF00", "#0000FF", "#FFA500", "#800080", "#FF1493", "#1E90FF"];
    return Array.from({ length: count }, (_, i) => colors[i % colors.length]);
}; // Different colors for segments

const RouteMap = ({ routeData }) => {

    const center = {
        lat: 42.454102,
        lng: -88.249931,
    };

    const [polylinePaths, setPolylinePaths] = useState([]);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [vehiclePosition, setVehiclePosition] = useState(center);
    const [iconColor, setIconColor] = useState('green')
    const map = useMap();
    const vehiclePos = useRef(null)
    const [showRoutesPolygon, setShowRoutesPolygon] = useState(localStorage.getItem('showRoutePolygon') == 'true' ?? false)
    const [shouldShowAddress, setShouldShowAddress] = useState(null)
    const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1224px)" });

    const routeColors = generateColors(routeData.length);

    function convertIntoNumber(value) {
        return typeof value === 'string' ? Number(value) : value;
    }

    async function fetchSnapToRoads(route_id) {
        try {
            let resp = await crudsService.getSnapRoads(route_id)
            if (resp.success) {
                return resp.data
            } else {
                return []
            }
        } catch (error) {
            console.error('fetchSnapToRoads error', error)
            return []
        }
    }

    async function processRoutes() {
        const allPolylines = await Promise.all(routeData.map(async (route, index) => {
            let waypoints = [];

            // Case 1: Live trip – use raw tracking points directly
            if (!route?.end_latitude) {
                waypoints = route?.RouteTrackingPoints?.map(point => ({
                    lat: convertIntoNumber(point.latitude),
                    lng: convertIntoNumber(point.longitude)
                })) || [];
            }

            // Case 2: Trip ended – try snapped road points
            else if (route?.RouteTrackingPoints?.length >= 2) {
                const snap_to_roads = await fetchSnapToRoads(route.route_id);

                if (Array.isArray(snap_to_roads) && snap_to_roads.length > 0) {
                    waypoints = snap_to_roads.map(point => ({
                        lat: convertIntoNumber(point.location.latitude),
                        lng: convertIntoNumber(point.location.longitude)
                    }));
                } else {
                    // Fallback to raw tracking points
                    waypoints = route.RouteTrackingPoints.map(point => ({
                        lat: convertIntoNumber(point.latitude),
                        lng: convertIntoNumber(point.longitude)
                    }));
                }
            }

            return waypoints;

        }));

        setPolylinePaths(allPolylines);
    }

    function findStopPoints(trackingPoints) {
        let stopPoints = [];
        for (let i = 1; i < trackingPoints.length; i++) {
            const prevTimestamp = new Date(trackingPoints[i - 1].timestamp);
            const currTimestamp = new Date(trackingPoints[i].timestamp);
            const timeDiff = (currTimestamp - prevTimestamp) / (1000 * 60); // Convert ms to minutes
            if (timeDiff >= 5) {
                stopPoints.push({ device: trackingPoints[i], diff: timeDiff.toFixed(2) });
            }
        }
        return stopPoints;
    }

    function findPausePoints(trackingPoints) {
        let pausePoints = [];
        for (let i = 1; i < trackingPoints.length; i++) {// Convert ms to minutes
            if (trackingPoints[i]?.status_type_id && trackingPoints[i]?.status_type_id == 30) {
                const prevTimestamp = new Date(trackingPoints[i - 1].timestamp);
                const currTimestamp = new Date(trackingPoints[i].timestamp);
                const timeDiff = (currTimestamp - prevTimestamp) / (1000 * 60); // Convert ms to minutes
                pausePoints.push({ device: trackingPoints[i], diff: timeDiff.toFixed(2) });
            }
        }
        return pausePoints;
    }

    function wayPointTrack(trackingPoints) {
        let stopPoints = [];
        for (let i = 1; i < trackingPoints.length - 1; i++) {
            const device = trackingPoints[i];

            if (device?.status_type_id && device?.status_type_id != 30) {
                stopPoints.push({ device, sequence: `#${i} point` })
            }

        }
        return stopPoints;
    }

    function handleMarkerClick(routeId, type, position, info = null) {
        setSelectedMarker({ type, position, info })
        map?.setCenter({ lat: parseFloat(position.latitude), lng: parseFloat(position.longitude) });
    }
    function removeMarkerClick() {
        setSelectedMarker(null)
    }

    useEffect(() => {
        if (routeData.length > 0 && map) {
            processRoutes();
            let point = routeData[0]?.RouteTrackingPoints[0]
            map.setCenter({ lat: Number(point.latitude), lng: Number(point.longitude) })
        } else {
            setPolylinePaths([])
        }
    }, [routeData, map]);


    // if (loadError) return <div>Error loading maps</div>;
    // if (!isLoaded) return <div>Loading...</div>;

    return (
        <Map
            style={mapContainerStyle}
            defaultCenter={center}
            defaultZoom={15}
            gestureHandling={'greedy'}
            // disableDefaultUI={true}
            mapId={'d089c1a7f8831a36'}
        >
             {selectedMarker && (
                <Card
                    sx={{
                        position: "absolute",
                        bottom: 16,
                        left: 16,
                        padding: 2,
                        backgroundColor: "white",
                    }}
                >
                    <MDTypography sx={{ fontSize: "20px" }}>
                        {selectedMarker.info ? <h5 style={{ textAlign: 'center' }}>{selectedMarker.info}(Route: {selectedMarker.position.route_id})</h5> : <h5 style={{ textAlign: 'center' }}>{selectedMarker.type.charAt(0).toUpperCase() + selectedMarker.type.slice(1)} Point (Route: {selectedMarker.position.route_id})</h5>}
                    </MDTypography>
                    <MDTypography sx={{ fontSize: "20px" }}>
                        Time: {moment(selectedMarker.position.timestamp).format("MMMM DD, YYYY h:mm:ss A")}
                    </MDTypography>
                    <MDTypography sx={{ fontSize: "20px" }}>
                        Latitude: {selectedMarker.position.latitude}
                    </MDTypography>
                    <MDTypography sx={{ fontSize: "20px" }}>
                        Longitude: {selectedMarker.position.longitude}
                    </MDTypography>
                    <MDTypography sx={{ fontSize: "20px" }}>
                        Speed: {convertUtils(selectedMarker.position.speed).from('km/h').to('m/h').toFixed(2)} m/h
                    </MDTypography>
                    <MDTypography sx={{ fontSize: "20px" }}>
                        Heading: {selectedMarker.position.heading}
                    </MDTypography>
                    <MDButton color="dark" onClick={() => removeMarkerClick()}>
                        Close
                    </MDButton>
                </Card>
            )}
            {polylinePaths.map((path, index) => (
                <Polyline
                    key={index}
                    encodedPath={path}
                    // options={{
                    strokeColor={routeColors[index]}
                    strokeOpacity={0.8}
                    strokeWeight={3}
                    strokeDasharray={[10, 10]}
                />
            ))}
            { routeData.map((route, index) => {
                const trackingPoints = route?.RouteTrackingPoints || [];
                const end_latitude = route?.end_latitude || null;
                const startPoint = trackingPoints[0]; // First in time order
                const endPoint = trackingPoints[trackingPoints.length - 1]; // Last in time order
                const stopPoints = findStopPoints(trackingPoints); // Find stops
                const pausePoints = findPausePoints(trackingPoints); // Find stops
                const wayPoints = wayPointTrack(trackingPoints); // Find stops
                return (
                    <React.Fragment key={route.route_id}>


                        {/* Start Marker */}
                        {startPoint && (
                            <Marker
                                position={{
                                    lat: convertIntoNumber(startPoint.latitude),
                                    lng: convertIntoNumber(startPoint.longitude)
                                }}
                                icon={{
                                    path: google.maps.SymbolPath.CIRCLE,
                                    fillColor: '#28a745',
                                    fillOpacity: 0.9,
                                    strokeWeight: 2,
                                    strokeColor: '#ffffff',
                                    scale: 10
                                }}
                                title="Start Point"
                                animation={google.maps.Animation.DROP}
                                onClick={() => handleMarkerClick(route.route_id, 'start', startPoint)}
                            />
                        )}

                        {/* End Marker */}
                        {(endPoint && end_latitude) && (
                            <Marker
                                position={{
                                    lat: convertIntoNumber(endPoint.latitude),
                                    lng: convertIntoNumber(endPoint.longitude)
                                }}
                                icon={{
                                    path: google.maps.SymbolPath.CIRCLE,
                                    fillColor: '#dc3545',
                                    fillOpacity: 0.9,
                                    strokeWeight: 2,
                                    strokeColor: '#ffffff',
                                    scale: 10
                                }}
                                title="End Point"
                                animation={google.maps.Animation.DROP}
                                onClick={() => handleMarkerClick(route.route_id, 'end', endPoint)}
                            />
                        )}

                        {/* Stop Markers (Stops longer than 10 min) */}
                        {stopPoints.map((point, i) => (
                            <Marker
                                key={`stop-${i}`}
                                position={{
                                    lat: convertIntoNumber(point.device.latitude),
                                    lng: convertIntoNumber(point.device.longitude)
                                }}
                                icon={{
                                    url: "https://maps.gstatic.com/mapfiles/ms2/micons/parkinglot.png", // Yellow Stop Icon
                                    scaledSize: new window.google.maps.Size(25, 25)
                                }}
                                title={`Stop Point (${point.diff} min)`}
                                onClick={() => handleMarkerClick(route.route_id, 'stop', point.device, `Stopped for ${point.diff} min`)}
                            />
                        ))}
                        {/* Stop Markers (Stops longer than 10 min) */}
                        {pausePoints.map((point, i) => (
                            <AdvancedMarker key={`pause-${i}`} position={{
                                lat: convertIntoNumber(point.device.latitude),
                                lng: convertIntoNumber(point.device.longitude)
                            }} onClick={() => handleMarkerClick(route.route_id, 'pause', point.device, `Paused for ${point.diff} min`)} >
                                <div>
                                    <i className={icons.class_name} style={{ fontSize: '24px', color: 'orange' }} />
                                </div>
                            </AdvancedMarker>
                        ))}
                        {/* Way Point track */}
                        {wayPoints.map((point, i) => (
                            <Marker
                                key={`way-${i}`}
                                position={{
                                    lat: convertIntoNumber(point.device.latitude),
                                    lng: convertIntoNumber(point.device.longitude)
                                }}
                                icon={{
                                    path: google.maps.SymbolPath.CIRCLE,
                                    fillColor: '#3388ff',
                                    fillOpacity: 0.7,
                                    strokeWeight: 1,
                                    strokeColor: '#ffffff',
                                    scale: 5
                                }}
                                title={`Waypoint (${point.device.point_id}, ${point.sequence})`}
                                onClick={() => handleMarkerClick(route.route_id, 'waypoint', point.device, `Waypoint (${point.device.point_id}, ${point.sequence})`)}
                            />
                        ))}
                    </React.Fragment>
                );
            })}
        </Map>
    );
};

export default RouteMap;